import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TMemberCapacitySummary,
  TProjectCapacityRisksResponse,
  TWorkspaceCapacityRisksResponse,
} from "@/types/project-capacity";
import type {
  TWorkspaceCapacityGroupBy,
  TWorkspaceCapacitySummary,
  TWorkspaceCapacityTotals,
} from "@/types/workspace-capacity";

type TCapacityRequestOptions = {
  includeTotals?: boolean;
  memberPageSize?: number;
  memberCursor?: string | null;
  memberIds?: string[];
};

function appendMemberIds(params: URLSearchParams, memberIds: string[] | undefined): void {
  if (memberIds?.length) params.set("member_ids", memberIds.join(","));
}

function appendCapacityPageOptions(
  params: URLSearchParams,
  options: TCapacityRequestOptions,
): void {
  if (options.includeTotals === false) params.set("include_totals", "false");
  if (typeof options.memberPageSize === "number")
    params.set("member_page_size", String(options.memberPageSize));
  if (options.memberCursor) params.set("member_cursor", options.memberCursor);
  appendMemberIds(params, options.memberIds);
}

export class ProjectCapacityService extends ApiService {
  async getMemberCapacitySummary(
    workspaceSlug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TMemberCapacitySummary> {
    return this.get(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/member-capacity-summary/`,
      config,
    );
  }

  async getProjectCapacity(
    workspaceSlug: string,
    projectId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    options: TCapacityRequestOptions = {},
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceCapacitySummary> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    appendCapacityPageOptions(params, options);
    return this.get(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/capacity-allocations/?${params.toString()}`,
      config,
    );
  }

  async getProjectCapacityTotals(
    workspaceSlug: string,
    projectId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceCapacityTotals> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    return this.get(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/capacity-allocations/totals/?${params.toString()}`,
      config,
    );
  }

  /**
   * A project's capacity risks over a window.
   *
   * `memberIds` narrows every risk to those people, and moves BOTH sides of the
   * capacity gap — the remaining work and the free hours it is measured against — so a
   * caller showing one team is answered about that team rather than about everyone on
   * the project. Omit it to ask about the whole project.
   */
  async getProjectCapacityRisks(
    workspaceSlug: string,
    projectId: string,
    startDate: string,
    endDate: string,
    memberIds?: readonly string[],
    config?: ApiRequestConfig,
  ): Promise<TProjectCapacityRisksResponse> {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (memberIds?.length) params.set("member_ids", memberIds.join(","));
    return this.get(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/capacity-risks/?${params.toString()}`,
      config,
    );
  }

  async getWorkspaceCapacityRisks(
    workspaceSlug: string,
    projectIds: readonly string[],
    startDate: string,
    endDate: string,
    memberIds?: readonly string[],
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceCapacityRisksResponse> {
    const params = new URLSearchParams({
      project_ids: projectIds.join(","),
      start_date: startDate,
      end_date: endDate,
    });
    if (memberIds?.length) params.set("member_ids", memberIds.join(","));
    return this.get(
      `/api/workspaces/${workspaceSlug}/capacity-risks/?${params.toString()}`,
      config,
    );
  }

  async getProgramCapacityTotals(
    workspaceSlug: string,
    programId: string,
    startDate: string,
    endDate: string,
    groupBy: TWorkspaceCapacityGroupBy = "day",
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceCapacityTotals> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      group_by: groupBy,
    });
    return this.get(
      `/api/workspaces/${workspaceSlug}/programs/${programId}/capacity-allocations/totals/?${params.toString()}`,
      config,
    );
  }
}
